import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  CAPTURE_TIMEOUT_SEC,
  captureFingerprint,
  checkDeviceStatus,
  classifyFingerprintScanError,
  fingerprintImageSrc,
  formatMatchPercent,
  getMinVerifyMatchScore,
  pickVerifySourceTemplate,
  verifyFingerprint,
} from "../../utils/mantraFingerprint";
import FingerprintInstallerDownloads from "./FingerprintInstallerDownloads";

const MIN_QUALITY_PERCENT = 75;
const PREPARE_WAIT_MS = 2000;
const PREPARE_PLACE_FINGER_MS = 2500;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fingerprint verification modal.
 * Captures the current finger, then POST /api/Fingerprint/verify:
 *   SourceTemplate = live scan
 *   TargetTemplate = enrolled fingerprint from viewDriver
 * This is one-to-one Match / No Match only (not Search).
 */
const FingerprintVerifyModal = ({
  open,
  onClose,
  driverId,
  driverName = "",
  group,
  targetTemplate = "",
  onVerified,
  autoScan = true,
}) => {
  const [device, setDevice] = useState(null);
  const [checkingDevice, setCheckingDevice] = useState(false);
  const [capture, setCapture] = useState(null);
  const [match, setMatch] = useState(null);
  const [scanError, setScanError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(null);
  /** @type {'idle' | 'wait' | 'place' | 'scanning' | 'verifying' | 'submitting'} */
  const [scanPhase, setScanPhase] = useState("idle");
  const scanAbortRef = useRef(false);
  const autoScanStartedRef = useRef(false);

  const deviceConnected = Boolean(device?.deviceConnected);
  const deviceChecked = device != null;
  const busy =
    scanPhase === "wait" ||
    scanPhase === "place" ||
    scanPhase === "scanning" ||
    scanPhase === "verifying" ||
    scanPhase === "submitting" ||
    checkingDevice;

  const previewSrc = fingerprintImageSrc(capture?.bitmapData);
  const quality = capture?.quality;
  const qualityOk =
    quality != null && Number.isFinite(quality) && quality >= MIN_QUALITY_PERCENT;
  const matched = match?.matched === true;

  const runDeviceCheck = useCallback(async () => {
    setCheckingDevice(true);
    try {
      const status = await checkDeviceStatus();
      setDevice(status);
      return status;
    } finally {
      setCheckingDevice(false);
    }
  }, []);

  const resetFlow = useCallback(() => {
    scanAbortRef.current = true;
    setScanPhase("idle");
    setCapture(null);
    setMatch(null);
    setScanError("");
    setSecondsLeft(null);
  }, []);

  const handleClose = () => {
    if (busy) return;
    resetFlow();
    autoScanStartedRef.current = false;
    onClose();
  };

  const runScan = useCallback(async () => {
    if (!String(targetTemplate || "").trim()) {
      toast.error(
        "This driver's enrolled fingerprint is missing. Load the driver record and try again."
      );
      return;
    }

    const status = await runDeviceCheck();
    if (!status.deviceConnected) {
      setScanError(
        status.statusMessage ||
          "Scanner not connected. Check the device and tap Rescan."
      );
      setScanPhase("idle");
      return;
    }

    scanAbortRef.current = false;
    setCapture(null);
    setMatch(null);
    setScanError("");

    setScanPhase("wait");
    await sleep(PREPARE_WAIT_MS);
    if (scanAbortRef.current) return;

    setScanPhase("place");
    await sleep(PREPARE_PLACE_FINGER_MS);
    if (scanAbortRef.current) return;

    setScanPhase("scanning");
    try {
      const captured = await captureFingerprint({
        deviceModel: status.deviceModel,
      });
      if (scanAbortRef.current) return;
      setCapture(captured);

      if (
        captured.quality != null &&
        Number.isFinite(captured.quality) &&
        captured.quality < MIN_QUALITY_PERCENT
      ) {
        setScanError(
          `Quality ${captured.quality}% is too low. Need at least ${MIN_QUALITY_PERCENT}%. Rescan.`
        );
        return;
      }

      const savedTemplate = String(targetTemplate || "").trim();
      const liveTemplate = pickVerifySourceTemplate(captured, savedTemplate);
      if (!savedTemplate) {
        setScanError(
          "This driver's enrolled fingerprint template is missing. Close and try again."
        );
        toast.error("Enrolled fingerprint not found for this driver.");
        return;
      }
      if (!liveTemplate) {
        setScanError("Live fingerprint template is missing. Tap Rescan.");
        return;
      }

      setScanPhase("verifying");
      const result = await verifyFingerprint({
        sourceTemplate: liveTemplate,
        targetTemplate: savedTemplate,
      });
      if (scanAbortRef.current) return;

      const minScore = getMinVerifyMatchScore();
      const scoreOk =
        result.score == null || result.score >= minScore;
      const isMatch = result.matched === true && scoreOk;
      setMatch({ ...result, matched: isMatch });
      if (!isMatch) {
        const matchPercent = formatMatchPercent(result.score);
        const minPercent = formatMatchPercent(minScore);
        const scoreText =
          matchPercent != null
            ? ` Match ${matchPercent}% (need at least ${minPercent}%).`
            : "";
        setScanError(
          `Fingerprint did not match this driver.${scoreText} Place the enrolled finger and tap Rescan.`
        );
        toast.error("Fingerprint did not match this driver.");
      }
    } catch (err) {
      console.error(err);
      if (!scanAbortRef.current) {
        const classified = classifyFingerprintScanError(err);
        setScanError(classified.message);
        toast.error(classified.message);
      }
    } finally {
      if (!scanAbortRef.current) setScanPhase("idle");
    }
  }, [runDeviceCheck, targetTemplate]);

  useEffect(() => {
    if (!open) {
      autoScanStartedRef.current = false;
      return;
    }
    if (!String(targetTemplate || "").trim()) return;
    if (autoScanStartedRef.current) return;

    scanAbortRef.current = false;
    setCapture(null);
    setMatch(null);
    setScanError("");
    setScanPhase("idle");

    if (!autoScan) return;

    autoScanStartedRef.current = true;
    void runScan();
  }, [open, autoScan, runScan, targetTemplate]);

  useEffect(() => {
    if (scanPhase !== "scanning") {
      setSecondsLeft(null);
      return undefined;
    }
    setSecondsLeft(CAPTURE_TIMEOUT_SEC);
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev == null || prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [scanPhase]);

  const submitVerified = async () => {
    if (!match || !matched || !capture) return;

    setScanPhase("submitting");
    try {
      await onVerified?.({
        fingerprintSignature: (match.templateData || capture.bitmapData).trim(),
        bitmapData: capture.bitmapData,
        personId: match.personId || String(driverId || "").trim(),
        score: match.score,
      });
      resetFlow();
      autoScanStartedRef.current = false;
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message?.trim() || "Could not verify fingerprint.");
      setScanError(err?.message?.trim() || "Payment failed. You can rescan and try again.");
      setScanPhase("idle");
    }
  };

  const placeholderText = () => {
    if (previewSrc) return "";
    if (scanPhase === "wait") return "Please wait…";
    if (scanPhase === "place") {
      return "Place your finger firmly on the scanner and hold still.";
    }
    if (scanPhase === "scanning") return "Scanning your fingerprint…";
    if (scanError) return scanError;
    if (scanPhase === "verifying" || scanPhase === "submitting") {
      return "Please wait…";
    }
    return "No fingerprint scanned yet";
  };

  const phaseHint = () => {
    if (scanPhase === "wait") return "Getting scanner ready. Please wait a moment.";
    if (scanPhase === "place") return "Ready to scan. Put your finger on the scanner now.";
    if (scanPhase === "scanning") return "Do not lift your finger.";
    if (scanPhase === "verifying") return "Verifying fingerprint with the server…";
    if (scanPhase === "submitting") return "Submitting…";
    return null;
  };

  const primaryActionLabel = () => {
    if (scanPhase === "wait") return "Please wait…";
    if (scanPhase === "place") return "Place your finger…";
    if (scanPhase === "scanning") {
      return secondsLeft != null
        ? `Scanning… ${secondsLeft}s`
        : "Scanning…";
    }
    if (scanPhase === "verifying") return "Verifying…";
    if (scanPhase === "submitting") return "Submitting…";
    if (match && matched && qualityOk && !scanError) return "Confirm payment";
    if (capture || scanError) return "Rescan";
    return "Start scan";
  };

  const handlePrimaryAction = () => {
    if (busy) return;
    if (match && matched && qualityOk && !scanError) {
      void submitVerified();
      return;
    }
    void runScan();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999] p-4">
      <div className="bg-white p-6 w-full max-w-md text-center font-UbuntuRegular rounded-lg shadow-lg">
        <h2 className="text-lg font-semibold mb-2">Awaiting verification</h2>
        <p className="text-sm text-gray-600 mb-1">
          1:1 verify: live scan vs enrolled fingerprint
        </p>
        {driverName ? (
          <p className="text-sm text-gray-800 font-UbuntuMedium mb-4">
            Driver: {driverName}
          </p>
        ) : (
          <div className="mb-4" />
        )}

        <div className="mx-auto mb-3 flex h-[200px] w-[160px] items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Scanned fingerprint"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="px-3 text-center text-sm text-gray-500">
              {placeholderText()}
            </span>
          )}
        </div>

        {scanPhase === "scanning" && secondsLeft != null ? (
          <p
            className={`text-2xl font-UbuntuMedium tabular-nums mb-2 ${
              secondsLeft <= 5 ? "text-danger" : "text-primary"
            }`}
            aria-live="polite"
          >
            {secondsLeft}s
          </p>
        ) : null}

        {deviceChecked ? (
          <p
            className={`text-sm font-UbuntuMedium m-0 mb-2 ${
              deviceConnected ? "text-emerald-700" : "text-amber-700"
            }`}
          >
            {device.statusMessage}
          </p>
        ) : null}

        <FingerprintInstallerDownloads
          visible={deviceChecked && !deviceConnected && scanPhase === "idle"}
        />

        {scanError && scanPhase === "idle" ? (
          <p className="text-sm font-UbuntuMedium text-amber-700 mb-3">{scanError}</p>
        ) : phaseHint() ? (
          <p className="text-sm font-UbuntuMedium text-primary mb-3" aria-live="polite">
            {phaseHint()}
          </p>
        ) : null}

        {matched && qualityOk && scanPhase === "idle" && !scanError ? (
          <div
            className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3"
            role="status"
          >
            <p className="text-sm font-UbuntuMedium text-emerald-800 m-0">
              Fingerprint verified
            </p>
            <p className="text-xs text-emerald-700 m-0 mt-1">
              {driverName
                ? `Matched with ${driverName}.`
                : "Matched with the enrolled fingerprint."}
              {match?.score != null
                ? ` Match ${formatMatchPercent(match.score)}%.`
                : ""}
            </p>
          </div>
        ) : capture && scanPhase === "idle" ? (
          <p className="text-sm font-UbuntuMedium mb-4 text-amber-700">
            Quality {quality}%
            {!qualityOk ? ` — need at least ${MIN_QUALITY_PERCENT}%` : ""}
            {qualityOk && match && !matched
              ? match.score != null
                ? ` · no match (${formatMatchPercent(match.score)}%, need ≥ ${formatMatchPercent(getMinVerifyMatchScore())}%)`
                : " · no match"
              : ""}
          </p>
        ) : (
          <div className="mb-4" />
        )}

        <button
          type="button"
          onClick={handlePrimaryAction}
          disabled={busy}
          className="btn !bg-danger border-danger text-white w-full !py-3 mb-2 !rounded-full disabled:opacity-60"
        >
          {primaryActionLabel()}
        </button>

        <button
          type="button"
          onClick={handleClose}
          disabled={busy}
          className="btn border !bg-primary w-full !py-3 !rounded-full disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default FingerprintVerifyModal;
